import type { ExcalidrawElement } from "../../packages/excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import { newElementWith } from "../../packages/excalidraw/element/mutateElement";
import { t } from "../../packages/excalidraw/i18n";

import { clearNotionRequestCache, notionDatabase } from "../services/notion";

import {
  buildDbMindmapTree,
  candidateGroupings,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import type {
  Grouping,
  NotionDbRow,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import type { MindmapCustomData } from "../../packages/mindmap-core/src/mindmapElements";
import { withMindmapGroupingCandidates } from "../../packages/mindmap-core/src/mindmapElements";
import { getMindmapApi } from "./mindmapApiRef";
import {
  rebuildMapFromTree,
  regroupToastMessage,
  tombstonedBlockIds,
} from "./mindmapRefresh";
import {
  clearRefreshInFlight,
  markRefreshInFlight,
} from "./mindmapRefreshBusy";

/**
 * Regrouping a database map, and the one-time nudge that points at it.
 *
 * A database map is generated FLAT (see `generateMindmapFromDatabase`), so
 * "group these rows by Status" is a post-generation act — but NOT an incremental
 * one. A grouped row's blockId embeds its group (`${groupId}::${rowId}`), so
 * changing the grouping changes every blockId on the map: routed through the
 * ordinary refresh it read as "remove everything, add everything", and refresh's
 * placement — which positions each add against its parent and only pushes down —
 * turned the map into a single column thousands of pixels tall.
 *
 * So a regroup rebuilds instead of diffing: fetch, build the tree, run the FULL
 * layout generation uses, and drop it back anchored on the root's current
 * position. The map keeps its `mapId`, its place on the canvas, its root's
 * bookkeeping and a single undo step; only the arrangement changes.
 */

const mindmapOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  el.customData?.mindmap as MindmapCustomData | undefined;

/** The map's root element (the pill whose blockId IS the source id). */
const rootOf = (
  elements: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement | undefined =>
  elements.find((el) => {
    const mm = mindmapOf(el);
    return mm?.mapId === mapId && !!mm.blockId && mm.blockId === mm.rootPageId;
  });

/** The root pill's label, kept as the rebuilt tree's root text: the database
 * query does not carry the database's own title. */
const rootLabelOf = (el: ExcalidrawElement): string =>
  String((el as unknown as { label?: string }).label || "Untitled");

/** Rewrite one element's mindmap stamp, preserving the rest of customData. */
const restamped = (
  el: ExcalidrawElement,
  next: MindmapCustomData,
): ExcalidrawElement =>
  newElementWith(el, {
    customData: { ...el.customData, mindmap: next },
  });

/**
 * The select/status properties this database could be grouped by, derived from
 * its rows. Cached like every other expand-time query, and degrading to an empty
 * list on failure: an unreachable database leaves the popover offering only
 * "None (flat)" rather than an error the user can do nothing about.
 */
export const mindmapGroupingCandidates = async (
  databaseId: string,
): Promise<Grouping[]> => {
  try {
    const res = await notionDatabase(databaseId, { cache: true });
    if (res.error || !res.data?.results) {
      return [];
    }
    return candidateGroupings(res.data.results as NotionDbRow[]);
  } catch {
    return [];
  }
};

/**
 * Set (or clear, for a flat regroup) the grouping property on EVERY element of
 * one map, returning the next scene array — or null when nothing would change.
 *
 * All of them, not just the root: the stamp is written map-wide at generation,
 * and refresh reads the first element that carries one. Leaving the root's old
 * value behind would make a later plain refresh silently regroup the map back.
 */
export const applyMapGrouping = (
  elements: readonly ExcalidrawElement[],
  mapId: string,
  groupingProperty: string | undefined,
): ExcalidrawElement[] | null => {
  let changed = false;
  const next = elements.map((el) => {
    const mm = mindmapOf(el);
    if (mm?.mapId !== mapId || mm.groupingProperty === groupingProperty) {
      return el;
    }
    changed = true;
    const { groupingProperty: _dropped, ...rest } = mm;
    return restamped(
      el,
      groupingProperty ? { ...rest, groupingProperty } : rest,
    );
  });
  return changed ? next : null;
};

/**
 * The candidate list to show for a map, without a fetch when the map carries
 * one. The popover used to query Notion on every open; the list is a fact about
 * the database, so it is stored on the root pill and read straight back.
 *
 * A map saved before that field existed (or one whose first refresh has not run)
 * falls back to one fetch, which is then stored — uncommitted, since remembering
 * what Notion answered is bookkeeping, not something to undo.
 */
export const loadMindmapGroupingCandidates = async (
  mapId: string,
  databaseId: string,
): Promise<Grouping[]> => {
  const candidates = await mindmapGroupingCandidates(databaseId);
  const api = getMindmapApi();
  if (api && candidates.length > 0) {
    const next = withMindmapGroupingCandidates(
      api.getSceneElementsIncludingDeleted(),
      mapId,
      candidates,
    );
    if (next) {
      api.updateScene({ elements: next, commitToHistory: false });
    }
  }
  return candidates;
};

/**
 * Regroup one database map: refetch its rows, rebuild its tree under the chosen
 * property (or flat), lay the whole thing out again around the root's current
 * position and commit it ONCE — one undo step for one user decision.
 *
 * The deletion ledger travels with the map but cannot suppress across a change
 * of grouping by blockId, because every grouped blockId embeds its group.
 * `rebuildMapFromTree` therefore matches a suppressed row by its ROW identity —
 * the trailing segment every database blockId ends with — so a row the user
 * deleted stays deleted whichever grouping it is viewed under. A deleted GROUP
 * pill has no counterpart under another grouping and is simply not carried: the
 * entry stays in the ledger (so it applies again if the user returns to that
 * grouping) but suppresses nothing here.
 */
export const regroupMindmap = async (
  api: ExcalidrawImperativeAPI,
  mapId: string,
  rootPageId: string,
  groupingProperty?: string,
): Promise<void> => {
  markRefreshInFlight(mapId);
  try {
    let rows: NotionDbRow[];
    try {
      const res = await notionDatabase(rootPageId);
      if (res.error || !res.data?.results) {
        api.setToast({ message: t("mindmap.refreshUnreachable") });
        return;
      }
      rows = res.data.results as NotionDbRow[];
    } catch {
      api.setToast({ message: t("mindmap.refreshUnreachable") });
      return;
    }

    const candidates = candidateGroupings(rows);
    const grouping = groupingProperty
      ? candidates.find((g) => g.propertyName === groupingProperty)
      : undefined;
    if (groupingProperty && !grouping) {
      api.setToast({ message: t("mindmap.refreshGroupingGone") });
      return;
    }

    // Read AFTER the fetch: the canvas may have moved on while it ran.
    const scene = api.getSceneElementsIncludingDeleted();
    const root = rootOf(scene, mapId);
    if (!root) {
      return;
    }

    const tree = buildDbMindmapTree(
      { id: rootPageId, title: rootLabelOf(root) },
      rows,
      grouping,
    );
    const next = rebuildMapFromTree(scene, {
      mapId,
      rootPageId,
      tree,
      source: "database",
      groupingProperty,
      suppressed: tombstonedBlockIds(scene, mapId),
      groupingCandidates: candidates,
    });
    if (!next) {
      return;
    }

    api.updateScene({ elements: next, commitToHistory: true });
    api.setToast({ message: regroupToastMessage(groupingProperty) });
    // Ungrouping can push a map past the flat threshold for the first time.
    maybeNudgeLargeFlatDatabase(api, mapId);
  } finally {
    clearRefreshInFlight(mapId);
    // The rows were read uncached; the expand cache still holds the old copy.
    clearNotionRequestCache();
  }
};

/**
 * Row count past which a flat database map is worth a nudge. Roughly the point
 * at which the root's fan-out stops fitting a screen at readable zoom.
 */
export const LARGE_FLAT_ROW_COUNT = 25;

/**
 * Toast once — ever, per map — when a flat database map is large enough that
 * grouping would help, pointing at the root pill's Group by control.
 *
 * The "already nudged" flag is stamped on the map root's `customData`, not
 * localStorage: it is a fact about this map, so it has to survive a reload,
 * travel with the scene when it is shared or duplicated, and disappear when the
 * map does. A localStorage key would repeat the nudge on another device and
 * accumulate entries for maps that no longer exist.
 */
export const maybeNudgeLargeFlatDatabase = (
  api: ExcalidrawImperativeAPI,
  mapId: string,
): void => {
  const scene = api.getSceneElementsIncludingDeleted();
  const root = rootOf(scene, mapId);
  const rootMm = root && mindmapOf(root);
  if (
    !root ||
    !rootMm ||
    rootMm.source !== "database" ||
    rootMm.groupingProperty ||
    rootMm.largeFlatNudged
  ) {
    return;
  }

  const rows = scene.filter((el) => {
    const mm = mindmapOf(el);
    return (
      !el.isDeleted &&
      mm?.mapId === mapId &&
      mm.role === "node" &&
      el.id !== root.id
    );
  }).length;
  if (rows <= LARGE_FLAT_ROW_COUNT) {
    return;
  }

  // Uncommitted: remembering that we spoke is bookkeeping, not a user action,
  // and must not sit between the generation and the user's first undo.
  api.updateScene({
    elements: scene.map((el) =>
      el.id === root.id
        ? restamped(el, { ...rootMm, largeFlatNudged: true })
        : el,
    ),
    commitToHistory: false,
  });
  api.setToast({ message: t("mindmap.largeFlatNudge"), closable: true });
};
